import { FunctionComponent, useEffect, useState } from 'react';
import {
	Box,
	Button,
	DateTimePicker,
	H2,
	HStack,
	Input,
	RadioButtonList,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import BackButton from 'components/common/BackButton/BackButton';
import SectionDivider from 'components/common/SectionDivider';
import moment, { Moment } from 'moment';
import {
	PermissionSet,
	SubscriptionType,
} from 'domain/subscription/interfaces/Subscription';
import useSubmit, { POST } from 'hooks/useSubmit';
import {
	ACCOUNT_API_COMPANIES,
	BUNDLES_GET_BUNDLE,
	SUBSCRIPTIONS_API_ROUTE,
} from 'apiRoutes';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { getTypeOptions } from './options';
import {
	ApplicationPermissions,
	useSubscriptionFormContext,
} from './SubscriptionFormContext';
import { SubscriberType } from 'domain/subscription/interfaces/SubscriberType';
import { CompanySelect } from 'components/companies/CompanySelect';
import { Company } from 'domain/company/Company';
import EditApplicationTabs from './EditApplicationTabs/EditApplicationTabs';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { RequestLoader } from 'components/common/Loader';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { useGet } from 'hooks/useGet';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';

const FormContainer = styled(Box)`
	max-width: 600px;
`;

const SubscriptionForm: FunctionComponent<{
	edit?: boolean;
	assignToCompanyId?: number;
	bundleId?: number;
	assignToSubscriber?: string;
	subscriberType?: SubscriberType;
}> = ({
	edit = false,
	assignToCompanyId = null,
	bundleId = undefined,
	assignToSubscriber,
	subscriberType = SubscriberType.Account,
}) => {
	const {
		subscriptionFormState,
		setSubscriptionFormState,
		activeSubscription,
		resetFormState,
	} = useSubscriptionFormContext();
	const navigate = useNavigate();

	const [submitFormData, submitFormStatus] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/${
			edit ? 'UpdateSubscription' : 'CreateSubscription'
		}`,
		POST,
		{
			messageOnError: 'An error occured, please try again!',
			messageOnSuccess: `Subscription saved`,
			showApiError: true,
		},
	);

	const [assignSeat] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/AssignSeat`,
		POST,
		{
			messageOnSuccess: 'User successfully assigned to seat',
			messageOnError: 'Error assigning seat',
			showApiError: true,
		},
	);

	const [assignSystemEntity] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/AssignSystemEntity`,
		POST,
		{
			messageOnSuccess: 'System entity successfully assigned to seat',
			messageOnError: 'Error assigning system entity',
			showApiError: true,
		},
	);

	const { data: companyOptions, status: companyLoadingStatus } =
		useGetOnMount<Company[]>(`${ACCOUNT_API_COMPANIES}/GetCompanies`);

	const effectiveBundleId = bundleId ?? activeSubscription?.bundleId;

	const { data: bundle, getData: getBundle } = useGet<SubscriptionBundle>(
		effectiveBundleId
			? `${BUNDLES_GET_BUNDLE}?bundleId=${effectiveBundleId}`
			: '',
	);

	const [selectedCompany, setSelectedCompany] = useState<Company>();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	useEffect(() => {
		if (effectiveBundleId) {
			getBundle();
		}
	}, [effectiveBundleId]);

	useEffect(() => {
		if (!edit) resetFormState();

		if (activeSubscription?.companyId) {
			const company = companyOptions?.find(
				(c) => c.id === activeSubscription.companyId,
			);
			if (company) {
				setSelectedCompany(company);
			}
		} else if (assignToCompanyId) {
			const company = companyOptions?.find(
				(c) => c.id === assignToCompanyId,
			);
			if (company) {
				setSelectedCompany(company);
			}
		}
	}, [activeSubscription, assignToCompanyId, companyOptions]);

	const onEndDateChange = (newValue: Moment) => {
		setSubscriptionFormState({
			...subscriptionFormState,
			endDate: newValue.utc().startOf('day'),
		});
	};

	const onPermissionsAdded = (permission: ApplicationPermissions) => {
		const newPermissions = subscriptionFormState.applicationPermissions;
		newPermissions.push(permission);

		setSubscriptionFormState({
			...subscriptionFormState,
			applicationPermissions: newPermissions,
		});
	};

	const onPermissionsEdited = (permission: ApplicationPermissions) => {
		const newPermissions = subscriptionFormState.applicationPermissions;
		const index = newPermissions.findIndex(
			(p) => p.application.id === permission.application.id,
		);
		if (index !== -1) {
			newPermissions[index] = permission;
		}

		setSubscriptionFormState({
			...subscriptionFormState,
			applicationPermissions: newPermissions,
		});
	};

	const onPermissionsRemoved = (applicationId: string) => {
		const newPermissions =
			subscriptionFormState.applicationPermissions.filter(
				(p) => p.application.id !== applicationId,
			);

		setSubscriptionFormState({
			...subscriptionFormState,
			applicationPermissions: newPermissions,
		});
	};

	const getPermissionSets = () => {
		const sets: Record<string, PermissionSet> = {};

		subscriptionFormState.applicationPermissions.forEach((p) => {
			sets[p.application.id] = {
				plan: p.plan.code,
				countries: p.countries.map((c) => c.code),
				addons: p.addons.map((a) => a.code),
			};
		});

		return sets;
	};

	const onTypeSelectionChanged = (selected: SubscriptionType) => {
		let endDate = subscriptionFormState.endDate;

		if (!endDate && selected == SubscriptionType.Trial)
			endDate = moment.utc().startOf('day').add(14, 'day');

		setSubscriptionFormState({
			...subscriptionFormState,
			endDate: endDate,
			type: selected,
		});
	};

	const onCompanySelectedFromList = (companyId: number) => {
		const company = companyOptions.find((c) => c.id === companyId);

		setSelectedCompany(company);
	};

	const onDescriptionChanged = (description: string) => {
		setSubscriptionFormState({
			...subscriptionFormState,
			description: description,
		});
	};

	const onSubmit = async () => {
		const body = edit
			? {
					companyId: selectedCompany ? selectedCompany.id : null,
					subscriptionId: activeSubscription?.id,
					applicationPermissions: getPermissionSets(),
					endDate: subscriptionFormState.endDate,
					seats: Number(subscriptionFormState.seats),
					type: subscriptionFormState.type,
					subscriberType: subscriberType,
					description: subscriptionFormState.description,
					bundleId: effectiveBundleId,
			  }
			: {
					applicationPermissions: getPermissionSets(),
					companyId: selectedCompany ? selectedCompany.id : null,
					endDate: subscriptionFormState.endDate,
					seats: Number(subscriptionFormState.seats),
					type: subscriptionFormState.type,
					subscriberType: subscriberType,
					description: subscriptionFormState.description,
					bundleId: bundleId,
			  };

		const result = await submitFormData(body);

		if (result.success) {
			if (
				assignToSubscriber &&
				subscriberType === SubscriberType.Account
			) {
				await assignSeat({
					subscriberId: assignToSubscriber,
					subscriptionId: result.data,
				});
			} else if (subscriberType === SubscriberType.SystemEntity) {
				await assignSystemEntity({
					systemEntityId: assignToSubscriber,
					subscriptionId: result.data,
				});
			}

			edit
				? navigate(`/subscriptions/${activeSubscription?.id}`)
				: navigate(`/subscriptions/${result.data}`);
		}
	};

	const isValidEndDate = (current: Moment) => {
		if (current.isBefore(moment().add(1, 'day'), 'day')) return false;
		if (bundle?.endDate && current.isAfter(moment(bundle.endDate), 'day'))
			return false;
		return true;
	};

	const hasPermissions =
		Object.keys(subscriptionFormState.applicationPermissions).length > 0;

	const isPaidType = subscriptionFormState.type === SubscriptionType.Paid;
	const companyRequirementMet = isPaidType ? selectedCompany : true;

	const endDateExceedsBundle =
		!!bundle?.endDate &&
		!!subscriptionFormState.endDate &&
		subscriptionFormState.endDate.isAfter(moment(bundle.endDate), 'day');

	const canSubmit =
		hasPermissions &&
		subscriptionFormState.seats > 0 &&
		subscriptionFormState.type &&
		companyRequirementMet &&
		!endDateExceedsBundle;

	const disableEditSubscriptionType = isRestrictedToRole(AdminRole.SALES);
	const disableEditCompany = !!effectiveBundleId;
	const disableEditSeats = isRestrictedToRole(AdminRole.SALES);

	return (
		<Box bg="bgMainContainer">
			<BackButton />
			<FormContainer bg="bgMainContainer">
				<VStack gap="md">
					<H2>Subscription Details</H2>
					<RadioButtonList
						label=""
						hideLabel
						required
						options={getTypeOptions()}
						onChange={onTypeSelectionChanged}
						value={subscriptionFormState.type}
						isDisabled={disableEditSubscriptionType}
					/>
					<Input
						label="Description"
						type="text"
						name="description"
						placeholder="Enter description"
						value={subscriptionFormState.description}
						onChange={(event) =>
							onDescriptionChanged(event.target.value)
						}
					/>
					<HStack fullWidth gap="md" wrap="nowrap">
						<VStack fullWidth wrap="nowrap" align="flex-start">
							<DateTimePicker
								label="End Date"
								keepLabelHeightWhenHidden
								utc
								isValidDate={(current: Moment) =>
									isValidEndDate(current)
								}
								initialViewDate={subscriptionFormState.endDate}
								onChange={onEndDateChange}
								value={subscriptionFormState.endDate}
								required={false}
								readonly
							/>
							{bundle?.endDate && (
								<Text
									size="xs"
									color={
										endDateExceedsBundle
											? 'negative'
											: 'textSubtle'
									}
								>
									{'Max: '}
									{moment(bundle.endDate).format(
										'DD MMM YYYY',
									)}
									{' (bundle end date)'}
								</Text>
							)}
							{subscriptionFormState.endDate && (
								<HStack fullWidth justify="flex-end">
									<Button
										onClick={() =>
											setSubscriptionFormState({
												...subscriptionFormState,
												endDate: undefined,
											})
										}
										variant="tertiary"
										text="Clear end date"
										size="sm"
									/>
								</HStack>
							)}
						</VStack>
						<Input
							type="number"
							label="Seats"
							name="seats"
							disabled={
								subscriberType === SubscriberType.SystemEntity
							}
							value={subscriptionFormState.seats}
							required
							onChange={(event) =>
								setSubscriptionFormState({
									...subscriptionFormState,
									seats: event.target.value,
								})
							}
							isDisabled={disableEditSeats}
						/>
					</HStack>
				</VStack>
				<SectionDivider />
				<VStack gap="xl">
					<H2>Ownership</H2>
					<RequestLoader status={companyLoadingStatus}>
						<VStack gap="md" fullWidth>
							<CompanySelect
								companies={companyOptions}
								disabled={disableEditCompany}
								value={
									selectedCompany ? selectedCompany.id : null
								}
								onChange={onCompanySelectedFromList}
								label="Select company"
								required={
									subscriptionFormState.type ==
									SubscriptionType.Paid
								}
							/>
							{selectedCompany && !effectiveBundleId && (
								<HStack fullWidth justify="flex-end">
									<Button
										onClick={() =>
											setSelectedCompany(undefined)
										}
										variant="tertiary"
										text="Clear company"
										size="sm"
										isDisabled={disableEditCompany}
									/>
								</HStack>
							)}
						</VStack>
					</RequestLoader>
				</VStack>
				<SectionDivider />
				<VStack gap="xl">
					<H2>Permissions</H2>
					<EditApplicationTabs
						applicationsPermissions={
							subscriptionFormState.applicationPermissions
						}
						onPermissionsAdded={onPermissionsAdded}
						onPermissionsEdited={onPermissionsEdited}
						onPermissionsRemoved={onPermissionsRemoved}
						subscriberType={subscriberType}
						bundle={bundle}
						companyId={selectedCompany?.id}
					/>
				</VStack>
				<SectionDivider />
				<HStack fullWidth justify="flex-end">
					<Button
						text={'Save subscription'}
						isDisabled={!canSubmit}
						onClick={onSubmit}
						type="submit"
						loadingText="Submitting"
						isLoading={submitFormStatus === RequestStatus.LOADING}
					/>
				</HStack>
			</FormContainer>
		</Box>
	);
};

export default SubscriptionForm;
